/**
 * returnToDashboard — the ONE way the app sends a user back to their own
 * dashboard after an action that creates a card or moves a card to a
 * different pipeline column (CLAUDE.md §39).
 *
 * The rule: any action that creates a file folder, preparation request or
 * referral, or moves one of them to a new column, returns the user to
 * their role dashboard once the whole success chain has resolved. Nothing
 * else navigates — in particular a note save never does.
 *
 * Route resolution. Every role's dashboard lives at the SAME route: `/`
 * renders `DashboardRouter` (src/App.jsx), which picks the Adviser,
 * Administration Assistant or New Business Assistant view from the ACTIVE
 * role in the auth context. That mapping is not duplicated here — this
 * helper only resolves the route (always `/` for a known role) and carries
 * the return state; the router does the rest.
 *
 * The navigation state is read on the dashboard by `useDashboardReturn`
 * (src/components/shared/DashboardReturnToast.jsx), which shows the toast,
 * scrolls the card into view, highlights it briefly and then clears the
 * state so a refresh does not replay it.
 *
 * No other component may build the dashboard route itself.
 */

/** The single dashboard route (see the header comment). */
export const DASHBOARD_ROUTE = '/';

/**
 * Card kinds a return can highlight. Annual review calendar cards are out of
 * scope for this mechanism; an unknown kind navigates without a highlight.
 */
export const RETURN_CARD_TYPES = ['file', 'prep', 'referral'];

/**
 * The dashboard route for a role. All three roles share `/` — the
 * DashboardRouter chooses the view. Kept as a function so every caller goes
 * through one resolution point.
 */
export function resolveDashboardRoute(/* role */) {
  return DASHBOARD_ROUTE;
}

/**
 * Navigate back to the caller's dashboard.
 *
 * @param {Function} navigate  — react-router's `navigate` from useNavigate()
 * @param {object}   options
 * @param {string}   options.role      — the ACTIVE role (from useAuth())
 * @param {string}   options.cardType  — 'file' | 'prep' | 'referral'
 * @param {string}   options.cardId    — the record id to highlight
 * @param {string}   options.message   — toast text, e.g. "Moved to Paused"
 * @param {boolean}  [options.replace] — true on creation forms so browser
 *                                       back does not reopen the completed
 *                                       form; false (push) on detail views
 */
export function returnToDashboard(navigate, { role, cardType, cardId, message, replace = false } = {}) {
  if (typeof navigate !== 'function') return;
  const highlight =
    RETURN_CARD_TYPES.includes(cardType) && cardId
      ? { cardType, cardId: String(cardId) }
      : null;
  navigate(resolveDashboardRoute(role), {
    replace: Boolean(replace),
    state: { toast: message || null, highlight },
  });
}

/**
 * Read a return payload from `location.state`. Returns null when the state
 * carries neither a toast nor a highlight (e.g. the delete-success state the
 * Adviser dashboard already handles, or no state at all).
 */
export function readReturnState(state) {
  if (!state || typeof state !== 'object') return null;
  const toast = typeof state.toast === 'string' && state.toast ? state.toast : null;
  const h = state.highlight;
  const highlight =
    h && RETURN_CARD_TYPES.includes(h.cardType) && h.cardId
      ? { cardType: h.cardType, cardId: String(h.cardId) }
      : null;
  if (!toast && !highlight) return null;
  return { toast, highlight };
}

/**
 * The DOM selector the dashboard uses to find a card. Every pipeline card
 * root (and the collapsed tabs in the fanned stacks) carries
 * `data-card-type` / `data-card-id` for exactly this lookup.
 */
export function cardSelector(cardType, cardId) {
  const safeId = String(cardId).replace(/["\\]/g, '');
  return `[data-card-type="${cardType}"][data-card-id="${safeId}"]`;
}

/** Standard toast wording for a column move. */
export function movedToMessage(columnLabel) {
  return `Moved to ${columnLabel}`;
}
